"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Check,
  ChevronDown,
  Sparkles,
  Settings,
  BarChart3,
  Package,
  Shield,
  Zap,
  Globe,
  Users,
  Clock,
  Layers,
  Database,
  Truck,
  Home,
  ClipboardCheck,
  TrendingUp,
  Heart,
  Star,
  LucideIcon
} from "lucide-react";

export interface FeatureCategory {
  title: string;
  items: string[];
  icon?: string;
}

interface FeatureListSectionProps {
  /** Section header */
  header?: {
    title: string;
    description?: string;
  };
  /** Array of feature categories with items */
  categories: FeatureCategory[];
  /** Optional className for additional styling */
  className?: string;
  /** Number of columns for the grid (default: 2) */
  cols?: 1 | 2 | 3;
  /** Enable expand/collapse for categories with many items */
  collapsible?: boolean;
  /** Initial items to show when collapsible (default: 4) */
  initialItems?: number;
}

// Category icon mapping - provides visual variety per category
const categoryIconMap: Record<string, LucideIcon> = {
  "turnover": Settings,
  "management": Settings,
  "software": Settings,
  "platform": Settings,
  "standards": Shield,
  "verification": Shield,
  "quality": Shield,
  "linen": Package,
  "towel": Package,
  "consumables": Package,
  "supplies": Package,
  "reporting": BarChart3,
  "analytics": BarChart3,
  "visibility": BarChart3,
  "operations": Zap,
  "support": Users,
  "team": Users,
  "delivery": Truck,
  "scheduling": Clock,
  "tracking": TrendingUp,
  "guest": Heart,
  "amenity": Sparkles,
  "cleaning": Home,
  "maintenance": ClipboardCheck,
  "default": Layers,
};

// Get icon based on category title keywords
function getCategoryIcon(title: string, iconKey?: string): LucideIcon {
  if (iconKey && categoryIconMap[iconKey]) {
    return categoryIconMap[iconKey];
  }
  
  const lowerTitle = title.toLowerCase();
  for (const [key, icon] of Object.entries(categoryIconMap)) {
    if (lowerTitle.includes(key)) {
      return icon;
    }
  }
  return categoryIconMap["default"];
}

// Feature item animation variants
const itemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.04,
      duration: 0.3,
    }
  })
};

/**
 * FeatureListSection Component
 * 
 * A professional, modern feature list with:
 * - Category icons for visual variety
 * - Feature count badges
 * - Subtle hover effects and animations
 * - Optional expand/collapse for long lists
 * - Grid-aligned with background system
 */
export default function FeatureListSection({
  header,
  categories,
  className = "",
  cols = 2,
  collapsible = false,
  initialItems = 4,
}: FeatureListSectionProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const toggleCategory = (index: number) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const colClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 lg:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Categories Grid */}
      <div className={`grid ${colClasses[cols]} gap-8 lg:gap-0`}>
        {categories.map((category, categoryIndex) => {
          const CategoryIcon = getCategoryIcon(category.title, category.icon);
          const isExpanded = expandedCategories.has(categoryIndex);
          const hasMoreItems = collapsible && category.items.length > initialItems;
          const visibleItems = collapsible && !isExpanded 
            ? category.items.slice(0, initialItems)
            : category.items;
          const hiddenCount = category.items.length - initialItems;

          return (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
              viewport={{ once: true }}
              className="relative group"
            >
              {/* Accent bar - themed */}
              <div 
                className="absolute left-0 top-0 w-0.5 h-12 rounded-full z-10 transition-all duration-300 group-hover:h-16"
                style={{ backgroundColor: 'var(--mininav-active-color, #14b8a6)' }}
              />
              
              {/* Category content */}
              <div className="px-4 lg:px-6 py-2 lg:py-0 mb-6 lg:mb-8">
                {/* Category Header */}
                <div className="flex items-start gap-3 mb-5">
                  {/* Category Icon */}
                  <div 
                    className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
                    style={{ 
                      backgroundColor: 'color-mix(in srgb, var(--mininav-active-color, #14b8a6) 12%, transparent)',
                    }}
                  >
                    <CategoryIcon 
                      className="w-5 h-5"
                      style={{ color: 'var(--mininav-active-color, #14b8a6)' }}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {/* Category Title */}
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-snug mb-1 font-satoshi">
                      {category.title}
                    </h3>
                    {/* Feature count badge */}
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                      {category.items.length} {category.items.length === 1 ? 'feature' : 'features'}
                    </span>
                  </div>
                </div>

                {/* Feature Items */}
                <ul className="space-y-2.5">
                  <AnimatePresence mode="sync">
                    {visibleItems.map((item, itemIndex) => (
                      <motion.li
                        key={item}
                        custom={itemIndex}
                        variants={itemVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="flex items-start gap-3 group/item py-1.5 px-2 -mx-2 rounded-lg transition-colors duration-200 hover:bg-gray-50/80"
                      >
                        {/* Check Icon with ring effect on hover */}
                        <div className="relative flex-shrink-0 mt-0.5">
                          <div 
                            className="w-5 h-5 rounded-full flex items-center justify-center transition-all duration-200 group-hover/item:scale-110"
                            style={{ 
                              backgroundColor: 'var(--mininav-active-color, #14b8a6)',
                            }}
                          >
                            <Check 
                              className="w-3 h-3 text-white"
                              strokeWidth={2.5}
                            />
                          </div>
                          {/* Subtle ring on hover */}
                          <div 
                            className="absolute inset-0 rounded-full opacity-0 group-hover/item:opacity-100 transition-opacity duration-200 -z-10 scale-150"
                            style={{ 
                              backgroundColor: 'color-mix(in srgb, var(--mininav-active-color, #14b8a6) 10%, transparent)',
                            }}
                          />
                        </div>
                        
                        {/* Feature Text */}
                        <span className="text-sm sm:text-base font-light text-gray-600 leading-relaxed group-hover/item:text-gray-900 transition-colors duration-200">
                          {item}
                        </span>
                      </motion.li>
                    ))}
                  </AnimatePresence>
                </ul>

                {/* Expand/Collapse Button */}
                {hasMoreItems && (
                  <motion.button
                    onClick={() => toggleCategory(categoryIndex)}
                    className="mt-4 flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 hover:opacity-80"
                    style={{ color: 'var(--mininav-active-color, #14b8a6)' }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>
                      {isExpanded ? 'Show less' : `Show ${hiddenCount} more`}
                    </span>
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </motion.div>
                  </motion.button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// Alternative compact variant for inline display
export function FeatureListCompact({
  features,
  className = "",
}: {
  features: string[];
  className?: string;
}) {

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ${className}`}>
      {features.map((feature, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.02 }}
          viewport={{ once: true }}
          className="flex items-center gap-2.5 group py-1.5 px-2 -mx-2 rounded-lg transition-colors duration-200 hover:bg-gray-50"
        >
          <div 
            className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-110"
            style={{ 
              backgroundColor: 'var(--mininav-active-color, #14b8a6)',
            }}
          >
            <Check 
              className="w-2.5 h-2.5 text-white"
              strokeWidth={3}
            />
          </div>
          <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors duration-200">{feature}</span>
        </motion.div>
      ))}
    </div>
  );
}

// Sample categories for demos
export const sampleFeatureCategories: FeatureCategory[] = [
  {
    title: "Turnover management software",
    items: [
      "Property onboarding with unit level standards and turnover rules",
      "Turnover scheduling aligned to arrivals and departures",
      "Live status updates across every unit",
      "Task tracking and completion flows built for field operations",
      "Proof of completion and audit trail records by unit",
    ]
  },
  {
    title: "Standards verification and quality checks",
    items: [
      "Standards verification checklists by property type",
      "Exception reporting for missing steps, damages, and issues",
      "Resolution tracking so problems are closed before check in",
      "Trend reporting to reduce repeat issues and rework",
    ]
  },
  {
    title: "Linen hire, towel hire, and consumables",
    items: [
      "Linen hire and towel hire mapped to property type",
      "Consumables replenishment by unit to prevent stockouts",
      "Usage tracking to support predictive replenishment",
      "Delivery scheduling with defined time slots",
    ]
  },
  {
    title: "Reporting and portfolio visibility",
    items: [
      "Portfolio wide visibility across units, buildings, and regions",
      "Reporting on completion rates, exceptions, and time to resolution",
      "Delivery and supply reporting by unit for reconciliation",
      "Cost control reporting to reduce emergency purchases",
    ]
  },
];
